import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Copy, Camera, Mail, MessageSquare, Crown, Loader2, ShieldCheck, X, Send } from "lucide-react";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { getTheme } from "@/lib/themes";
import { logError } from "@/lib/logger";
import { ProfileStats } from "@/components/ProfileStats";
import { UpgradeDialog } from "@/components/UpgradeDialog";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";

const SMS_PROVIDERS = [
  { key: "twilio", label: "Twilio", secretLabel: "Auth Token", secretHint: "Console dashboard → Auth Token",
    steps: ["Open Twilio Console → Phone Numbers → Manage → Active numbers.", "Click your SMS-enabled number.", "Under Messaging → 'A message comes in', choose Webhook, method HTTP POST.", "Paste the webhook URL above and Save."] },
  { key: "plivo", label: "Plivo", secretLabel: "Auth Token", secretHint: "Console → Keys & Credentials → Auth Token",
    steps: ["Open Plivo Console → Messaging → Numbers.", "Click your number to edit it.", "Set the Message URL to the webhook above with method POST.", "Save the number."] },
  { key: "vonage", label: "Vonage", secretLabel: "Signature Secret (optional)", secretHint: "Dashboard → Settings → Signature Secret",
    steps: ["Open Vonage Dashboard → Numbers → Your numbers.", "Click your number.", "Set the SMS inbound webhook URL to the URL above (HTTP POST).", "Save."] },
  { key: "telnyx", label: "Telnyx", secretLabel: "Public Key (Ed25519)", secretHint: "Portal → Account → Keys → Public Key",
    steps: ["Open Telnyx Portal → Messaging → Messaging Profiles.", "Edit your profile → Inbound Settings.", "Set the Webhook URL to the URL above.", "Save the profile."] },
  { key: "messagebird", label: "MessageBird", secretLabel: "Signing Key", secretHint: "Dashboard → Developers → Signing Key",
    steps: ["Open MessageBird/Bird Dashboard → your number or a Flow.", "Add a step/webhook that forwards inbound SMS.", "Set the destination URL to the webhook above (HTTP POST).", "Publish/save."] },
];

function timeAgo(iso) {
  if (!iso) return null;
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function ChannelHealth({ h, testid }) {
  return h?.last_at
    ? <div data-testid={testid} className="text-[11px] text-[#2e7d32] mt-0.5">● Last received {timeAgo(h.last_at)} · {h.count} total</div>
    : <div data-testid={testid} className="text-[11px] text-[#a89a86] mt-0.5">No messages received yet</div>;
}

function AvatarImage({ user }) {
  if (user?.avatar_url) return <img src={fileUrl(user.avatar_url)} alt="avatar" className="w-full h-full object-cover" />;
  if (user?.picture) return <img src={user.picture} alt="" className="w-full h-full object-cover" />;
  return <Camera className="w-10 h-10 text-[#c9b48f]" />;
}

export default function ProfilePage() {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const fileRef = useRef(null);
  const [stats, setStats] = useState(null);
  const [name, setName] = useState(user?.name || "");
  const [handle, setHandle] = useState(user?.handle || "");
  const [about, setAbout] = useState(user?.about_note || "");
  const [isPublic, setIsPublic] = useState(user?.is_public || false);
  const [autoCrumple, setAutoCrumple] = useState(String(user?.auto_crumple_days || 0));
  const [acceptAnyone, setAcceptAnyone] = useState((user?.accept_from || "anyone") === "anyone");
  const [phone, setPhone] = useState(user?.phone || "");
  const [codeSent, setCodeSent] = useState(false);
  const [code, setCode] = useState("");
  const [devCode, setDevCode] = useState(null);
  const [phoneBusy, setPhoneBusy] = useState(false);
  const [tgBusy, setTgBusy] = useState(false);
  const [smsConn, setSmsConn] = useState(null);
  const [smsProvider, setSmsProvider] = useState("twilio");
  const [smsNumber, setSmsNumber] = useState("");
  const [smsSecret, setSmsSecret] = useState("");
  const [smsBusy, setSmsBusy] = useState(false);
  const [health, setHealth] = useState({});
  const [referral, setReferral] = useState(null);
  const [cfg, setCfg] = useState(null);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const theme = getTheme(user?.theme);
  const isPro = user?.tier !== "free";

  useEffect(() => {
    api.get("/stats").then(({ data }) => setStats(data)).catch((err) => logError("Failed to load stats:", err));
    api.get("/config").then(({ data }) => setCfg(data)).catch(() => {});
    api.get("/sms/connection").then(({ data }) => setSmsConn(data)).catch(() => {});
    api.get("/channels/health").then(({ data }) => setHealth(data || {})).catch(() => {});
    api.get("/referral").then(({ data }) => setReferral(data)).catch(() => {});
  }, []);

  const unblock = async (s) => {
    try {
      const { data } = await api.post("/unblock", { sender: s });
      setUser({ ...user, blocked: data.blocked });
      toast.success("Unblocked");
    } catch (e) {
      toast.error("Could not unblock");
    }
  };

  const requestCode = async () => {
    setPhoneBusy(true);
    try {
      const { data } = await api.post("/profile/phone/request", { phone });
      setCodeSent(true);
      if (data.demo) { setDevCode(data.dev_code); toast.info("Demo mode — use the code shown below"); }
      else { setDevCode(null); toast.success("Code sent — check your texts"); }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send code");
    } finally { setPhoneBusy(false); }
  };

  const verifyCode = async () => {
    setPhoneBusy(true);
    try {
      const { data } = await api.post("/profile/phone/verify", { phone, code });
      setUser(data); setCodeSent(false); setCode(""); setDevCode(null);
      toast.success("Phone verified — texts now land on your board!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not verify");
    } finally { setPhoneBusy(false); }
  };

  const unlinkPhone = async () => {
    try {
      const { data } = await api.post("/profile/phone/unlink");
      setUser(data); setPhone(""); setCodeSent(false); setCode(""); setDevCode(null);
      toast.success("Phone unlinked");
    } catch (e) {
      toast.error("Could not unlink");
    }
  };

  const connectTelegram = async () => {
    setTgBusy(true);
    try {
      const { data } = await api.post("/telegram/link");
      window.open(data.url, "_blank", "noopener");
      toast.info("Tap Start in Telegram, then come back — this updates automatically.");
      let tries = 0;
      const poll = setInterval(async () => {
        tries += 1;
        try {
          const me = await api.get("/auth/me");
          if (me.data?.telegram_username || me.data?.telegram_chat_id) {
            setUser(me.data); clearInterval(poll); setTgBusy(false);
            toast.success("Telegram connected!");
          }
        } catch (_) {}
        if (tries >= 40) { clearInterval(poll); setTgBusy(false); }
      }, 3000);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Telegram isn't available yet");
      setTgBusy(false);
    }
  };

  const disconnectTelegram = async () => {
    try {
      const { data } = await api.post("/telegram/disconnect");
      setUser(data);
      toast.success("Telegram disconnected");
    } catch (e) {
      toast.error("Could not disconnect");
    }
  };

  const sendTest = async (channel) => {
    try {
      await api.post("/channels/test", { channel });
      toast.success(`Test ${channel} note sent — check your board!`);
      api.get("/channels/health").then(({ data }) => setHealth(data || {})).catch(() => {});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send test");
    }
  };

  const connectSms = async () => {
    setSmsBusy(true);
    try {
      const { data } = await api.post("/sms/connect", { provider: smsProvider, number: smsNumber, secret: smsSecret });
      setSmsConn(data); setSmsSecret("");
      toast.success("SMS provider connected — copy your webhook URL below");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not connect provider");
    } finally { setSmsBusy(false); }
  };

  const disconnectSms = async () => {
    try {
      const { data } = await api.post("/sms/disconnect");
      setSmsConn(data); setSmsNumber(""); setSmsSecret("");
      toast.success("SMS provider disconnected");
    } catch (e) {
      toast.error("Could not disconnect");
    }
  };

  const save = async () => {
    try {
      const { data } = await api.patch("/profile", { name, handle, about_note: about, is_public: isPublic, accept_from: acceptAnyone ? "anyone" : "contacts", auto_crumple_days: parseInt(autoCrumple, 10) || 0 });
      setUser(data);
      toast.success("Profile saved!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    }
  };

  const uploadAvatarFile = async (file) => {
    if (!file || !file.type?.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    setAvatarBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/profile/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUser({ ...user, avatar_url: data.avatar_url });
      toast.success("Photo saved — it now shows on your board!");
    } catch (err) {
      logError("Avatar upload failed:", err);
      toast.error("Upload failed");
    }
    setAvatarBusy(false);
  };

  const onAvatar = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    uploadAvatarFile(file);
  };

  const onAvatarDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    uploadAvatarFile(e.dataTransfer.files?.[0]);
  };

  const upgrade = () => setUpgradeOpen(true);

  const publicUrl = `${window.location.origin}/@${user?.handle}`;

  return (
    <div className="min-h-screen" style={{ background: theme.frame }}>
      <div className="max-w-5xl mx-auto px-4 sm:px-8 py-6">
        <button data-testid="back-board" onClick={() => navigate("/board")} className="flex items-center gap-1.5 font-display font-bold mb-6" style={{ color: theme.dark ? "#fdf6e3" : "#fdf6e3" }}>
          <ArrowLeft className="w-4 h-4" /> Back to board
        </button>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Avatar polaroid — click or drag a photo here */}
          <div className="md:col-span-1 flex flex-col items-center self-start">
            <div
              data-testid="avatar-dropzone"
              onClick={() => fileRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={onAvatarDrop}
              className={`w-full bg-white p-3 pb-10 paper-shadow relative rotate-[-2deg] cursor-pointer transition-all ${dragging ? "ring-4 ring-[#ff3b30] rotate-0" : ""}`}
            >
              <span className="thumbtack" />
              <div className="w-full aspect-square bg-[#efeae2] overflow-hidden flex items-center justify-center relative">
                <AvatarImage user={user} />
                {avatarBusy && <div className="absolute inset-0 bg-white/70 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-[#ff3b30]" /></div>}
                {!user?.avatar_url && !user?.picture && !avatarBusy && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center text-[#b7a98f] pointer-events-none">
                    <Camera className="w-8 h-8" />
                    <span className="text-xs font-display font-bold mt-1">Add a photo</span>
                  </div>
                )}
              </div>
              <p className="font-hand text-2xl text-center text-[#3a2f22] mt-3 absolute bottom-2 left-0 right-0 truncate px-3">{user?.name}</p>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onAvatar} data-testid="avatar-input" />
              <button data-testid="avatar-upload" onClick={(e) => { e.stopPropagation(); fileRef.current?.click(); }} className="absolute -bottom-3 right-3 w-9 h-9 rounded-full bg-[#ff3b30] text-white flex items-center justify-center paper-shadow hover:bg-[#e0271d]"><Camera className="w-4 h-4" /></button>
            </div>
            <p className="text-[11px] text-center text-[#d8ccb5] mt-4">Click or drag an image here. It shows on your board & profile.</p>
          </div>

          {/* Identity card */}
          <div className="md:col-span-3 bg-[#fbf6ec] grain rounded-2xl p-6 paper-shadow">
            <div className="flex items-center justify-between mb-4">
              <h1 className="font-display font-black text-2xl text-[#2b2620]">Your profile</h1>
              <Badge data-testid="tier-badge" className={`font-display font-bold ${isPro ? "bg-[#ff3b30]" : "bg-[#2b2620]"} text-white`}>{isPro ? <><Crown className="w-3 h-3 mr-1" />{user.tier.toUpperCase()}</> : "FREE"}</Badge>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="tag-label text-[#a0703c]">Display name</label>
                <Input data-testid="profile-name" value={name} onChange={(e) => setName(e.target.value)} className="bg-white mt-1" />
              </div>
              <div>
                <label className="tag-label text-[#a0703c]">Handle</label>
                <div className="flex items-center gap-1 mt-1">
                  <span className="text-[#6b6155] font-note">@</span>
                  <Input data-testid="profile-handle" value={handle} onChange={(e) => setHandle(e.target.value)} className="bg-white" />
                </div>
              </div>
            </div>
            <div className="mt-4">
              <label className="tag-label text-[#a0703c]">About me (pinned to your board)</label>
              <Textarea data-testid="profile-about" value={about} onChange={(e) => setAbout(e.target.value)} rows={2} className="bg-white mt-1 font-note text-base" />
            </div>
            <Button data-testid="profile-save" onClick={save} className="mt-4 rounded-full bg-[#2b2620] hover:bg-black text-[#fdf6e3] font-display font-bold">Save changes</Button>
          </div>

          {/* Channels */}
          <div className="md:col-span-2 bg-[#fbf6ec] grain rounded-2xl p-6 paper-shadow">
            <h2 className="font-display font-bold text-lg text-[#2b2620] mb-3">Your inbound channels</h2>
            <div className="flex items-center justify-between bg-white rounded-xl p-3 mb-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2"><Mail className="w-5 h-5 text-[#ff3b30]" /><span className="font-note font-bold text-[#2b2620] truncate">{user?.handle}@pinanote.app</span></div>
                <ChannelHealth h={health.email} testid="health-email" />
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button data-testid="test-email" onClick={() => sendTest("email")} className="text-[11px] font-bold text-[#a0703c] hover:text-[#ff3b30]">Send test</button>
                <button data-testid="copy-email" onClick={() => { navigator.clipboard?.writeText(`${user.handle}@pinanote.app`); toast.success("Copied!"); }} className="text-[#6b6155] hover:text-[#2b2620]"><Copy className="w-4 h-4" /></button>
              </div>
            </div>
            <div className="bg-white rounded-xl p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 min-w-0"><MessageSquare className="w-5 h-5 text-[#a0703c] shrink-0" /><span className="font-note font-bold text-[#2b2620] truncate">{cfg?.sms_number ? `Text ${cfg.sms_number}` : (isPro ? "SMS number — coming soon" : "SMS number (Pro)")}</span></div>
                {cfg?.sms_number ? (
                  <button data-testid="copy-sms" onClick={() => { navigator.clipboard?.writeText(cfg.sms_number); toast.success("Copied!"); }} className="text-[#6b6155] hover:text-[#2b2620] shrink-0"><Copy className="w-4 h-4" /></button>
                ) : (!isPro && <Button data-testid="upgrade-pro" onClick={upgrade} size="sm" className="rounded-full bg-[#ff3b30] hover:bg-[#e0271d] text-white font-display font-bold h-7 text-xs shrink-0">Upgrade</Button>)}
              </div>
              {isPro && (
                <div className="mt-3 border-t border-[#eee3ce] pt-3">
                  {user?.phone ? (
                    <div data-testid="phone-verified" className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <ShieldCheck className="w-5 h-5 text-[#2e7d32] shrink-0" />
                        <div className="min-w-0">
                          <div className="font-note font-bold text-[#2b2620] truncate">{user.phone}</div>
                          <div className="text-[11px] text-[#2e7d32]">Verified — texts from this number land on your board</div>
                        </div>
                      </div>
                      <button data-testid="unlink-phone" onClick={unlinkPhone} className="text-[#a89a86] hover:text-[#ff3b30] flex items-center gap-1 text-xs font-bold shrink-0"><X className="w-4 h-4" />Unlink</button>
                    </div>
                  ) : !codeSent ? (
                    <div>
                      <label className="tag-label text-[#a0703c]">Verify your mobile — texts from this number land on your board</label>
                      <div className="flex gap-2 mt-1">
                        <Input data-testid="profile-phone" placeholder="+1 415 555 0123" value={phone} onChange={(e) => setPhone(e.target.value)} className="bg-[#fbf6ec]" />
                        <Button data-testid="send-code-btn" onClick={requestCode} disabled={phoneBusy || !phone} className="rounded-full bg-[#a0703c] hover:bg-[#8a5f30] text-white font-display font-bold shrink-0">
                          {phoneBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send code"}
                        </Button>
                      </div>
                      <p className="text-[11px] text-[#a89a86] mt-1">We'll text you a 6-digit code to confirm the number is yours. Include your country code.</p>
                    </div>
                  ) : (
                    <div>
                      <label className="tag-label text-[#a0703c]">Enter the code we sent to {phone}</label>
                      <div className="flex gap-2 mt-1">
                        <Input data-testid="verify-code-input" placeholder="123456" inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value)} className="bg-[#fbf6ec]" />
                        <Button data-testid="verify-code-btn" onClick={verifyCode} disabled={phoneBusy || code.length < 4} className="rounded-full bg-[#2e7d32] hover:bg-[#256628] text-white font-display font-bold shrink-0">
                          {phoneBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verify"}
                        </Button>
                      </div>
                      {devCode && <p data-testid="dev-code" className="text-[11px] text-[#a0703c] mt-1 font-bold">Demo code: {devCode} (Twilio not configured — this is shown only until you add SMS credentials)</p>}
                      <button data-testid="change-phone-btn" onClick={() => { setCodeSent(false); setCode(""); setDevCode(null); }} className="text-[11px] text-[#6b6155] underline mt-1">Use a different number</button>
                    </div>
                  )}
                </div>
              )}
            </div>
            <div data-testid="telegram-channel" className="bg-white rounded-xl p-3 mt-3">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <Send className="w-5 h-5 text-[#229ED9] shrink-0" />
                  {user?.telegram_username || user?.telegram_chat_id ? (
                    <div className="min-w-0">
                      <div className="font-note font-bold text-[#2b2620] truncate">Telegram connected{user?.telegram_username ? ` (@${user.telegram_username})` : ""}</div>
                      <div className="text-[11px] text-[#2e7d32]">Messages to the bot appear on your board — free</div>
                      <ChannelHealth h={health.telegram} testid="health-telegram" />
                    </div>
                  ) : (
                    <div className="min-w-0">
                      <div className="font-note font-bold text-[#2b2620] truncate">Telegram — free</div>
                      <div className="text-[11px] text-[#a89a86]">Message a bot and it becomes a note. No phone number needed.</div>
                    </div>
                  )}
                </div>
                {user?.telegram_username || user?.telegram_chat_id ? (
                  <div className="flex items-center gap-2 shrink-0">
                    <button data-testid="test-telegram" onClick={() => sendTest("telegram")} className="text-[11px] font-bold text-[#229ED9] hover:text-[#1b8ec2]">Send test</button>
                    <button data-testid="disconnect-telegram" onClick={disconnectTelegram} className="text-[#a89a86] hover:text-[#ff3b30] flex items-center gap-1 text-xs font-bold"><X className="w-4 h-4" />Disconnect</button>
                  </div>
                ) : cfg?.telegram_enabled ? (
                  <Button data-testid="connect-telegram" onClick={connectTelegram} disabled={tgBusy} size="sm" className="rounded-full bg-[#229ED9] hover:bg-[#1b8ec2] text-white font-display font-bold h-7 text-xs shrink-0">
                    {tgBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Connect"}
                  </Button>
                ) : (
                  <span data-testid="telegram-soon" className="text-[11px] text-[#a89a86] shrink-0">Coming soon</span>
                )}
              </div>
            </div>
            {isPro && (
              <div data-testid="sms-provider-card" className="bg-white rounded-xl p-3 mt-3">
                <div className="flex items-center gap-2 mb-1"><MessageSquare className="w-5 h-5 text-[#a0703c] shrink-0" /><span className="font-note font-bold text-[#2b2620]">Bring your own SMS provider</span></div>
                <ChannelHealth h={health.sms} testid="health-sms" />
                {smsConn?.connected ? (
                  <div>
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <div className="text-[13px] text-[#2e7d32] font-bold">Connected: {SMS_PROVIDERS.find((p) => p.key === smsConn.provider)?.label || smsConn.provider}{smsConn.number ? ` (${smsConn.number})` : ""}{smsConn.has_secret ? " · signature on" : ""}</div>
                      <div className="flex items-center gap-2 shrink-0">
                        <button data-testid="test-sms" onClick={() => sendTest("sms")} className="text-[11px] font-bold text-[#a0703c] hover:text-[#ff3b30]">Send test</button>
                        <button data-testid="disconnect-sms" onClick={disconnectSms} className="text-[#a89a86] hover:text-[#ff3b30] flex items-center gap-1 text-xs font-bold"><X className="w-4 h-4" />Disconnect</button>
                      </div>
                    </div>
                    <label className="tag-label text-[#a0703c]">Paste this webhook URL into your provider's inbound-SMS settings</label>
                    <div className="flex gap-2 mt-1">
                      <Input data-testid="sms-webhook-url" readOnly value={smsConn.webhook_url} className="bg-[#fbf6ec] text-xs" />
                      <button data-testid="copy-sms-webhook" onClick={() => { navigator.clipboard?.writeText(smsConn.webhook_url); toast.success("Copied!"); }} className="text-[#6b6155] hover:text-[#2b2620] shrink-0"><Copy className="w-4 h-4" /></button>
                    </div>
                    <p className="text-[11px] text-[#a89a86] mt-2 mb-1 font-bold">How to paste it into {SMS_PROVIDERS.find((p) => p.key === smsConn.provider)?.label}:</p>
                    <ol data-testid="sms-guide" className="text-[11px] text-[#6b6155] list-decimal list-inside space-y-0.5">
                      {(SMS_PROVIDERS.find((p) => p.key === smsConn.provider)?.steps || []).map((s, i) => (<li key={i}>{s}</li>))}
                    </ol>
                    <p className="text-[11px] text-[#2e7d32] mt-1">Texts (and picture MMS) to your number then land on your board.</p>
                  </div>
                ) : (
                  <div>
                    <p className="text-[11px] text-[#a89a86] mb-2">Use your own Twilio, Plivo, Vonage, Telnyx or MessageBird number. Pick a provider, add your details, and we'll give you a webhook URL to paste in.</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      <Select value={smsProvider} onValueChange={setSmsProvider}>
                        <SelectTrigger data-testid="sms-provider-select" className="bg-[#fbf6ec]"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {SMS_PROVIDERS.map((p) => (<SelectItem key={p.key} value={p.key} data-testid={`sms-provider-${p.key}`}>{p.label}</SelectItem>))}
                        </SelectContent>
                      </Select>
                      <Input data-testid="sms-number" placeholder="Your SMS number +1..." value={smsNumber} onChange={(e) => setSmsNumber(e.target.value)} className="bg-[#fbf6ec]" />
                    </div>
                    <Input data-testid="sms-secret" type="password" placeholder={SMS_PROVIDERS.find((p) => p.key === smsProvider)?.secretLabel} value={smsSecret} onChange={(e) => setSmsSecret(e.target.value)} className="bg-[#fbf6ec] mt-2" />
                    <p className="text-[11px] text-[#a89a86] mt-1">{SMS_PROVIDERS.find((p) => p.key === smsProvider)?.secretHint} — enables signature verification. Optional; leave blank to rely on your private webhook URL.</p>
                    <Button data-testid="connect-sms" onClick={connectSms} disabled={smsBusy} className="rounded-full bg-[#a0703c] hover:bg-[#8a5f30] text-white font-display font-bold mt-2 w-full">
                      {smsBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Connect provider"}
                    </Button>
                  </div>
                )}
              </div>
            )}
            {referral && (
              <div data-testid="invite-card" className="bg-white rounded-xl p-3 mt-3">
                <div className="flex items-center gap-2 mb-1"><Send className="w-5 h-5 text-[#a0703c] shrink-0" /><span className="font-note font-bold text-[#2b2620]">Invite friends</span>{referral.count > 0 && <span className="ml-auto text-xs font-bold text-[#2e7d32]">{referral.count} joined 🎉</span>}</div>
                <p className="text-[11px] text-[#a89a86] mb-2">Share your link — when friends join, you both help the board grow.</p>
                <div className="flex gap-2">
                  <Input data-testid="invite-link" readOnly value={referral.link} className="bg-[#fbf6ec] text-xs" />
                  <button data-testid="copy-invite" onClick={() => { navigator.clipboard?.writeText(referral.link); toast.success("Invite link copied!"); }} className="text-[#6b6155] hover:text-[#2b2620] shrink-0"><Copy className="w-4 h-4" /></button>
                </div>
              </div>
            )}

            {(user?.blocked?.length > 0) && (
              <div data-testid="blocked-list" className="bg-white rounded-xl p-3 mt-3">
                <span className="tag-label text-[#a0703c]">Blocked senders</span>
                <div className="mt-1 space-y-1">
                  {user.blocked.map((b) => (
                    <div key={b} className="flex items-center justify-between gap-2">
                      <span className="font-note text-sm text-[#2b2620] truncate">{b}</span>
                      <button data-testid={`unblock-${b}`} onClick={() => unblock(b)} className="text-xs font-display font-bold text-[#a0703c] hover:text-[#ff3b30] shrink-0">Unblock</button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Privacy */}
          <div className="md:col-span-2 bg-[#fbf6ec] grain rounded-2xl p-6 paper-shadow">
            <h2 className="font-display font-bold text-lg text-[#2b2620] mb-3">Privacy</h2>
            <div className="flex items-center justify-between py-2">
              <div><p className="font-bold text-sm text-[#2b2620]">Public profile</p><p className="text-xs text-[#6b6155]">Show your board at /@{user?.handle}</p></div>
              <Switch data-testid="toggle-public" checked={isPublic} onCheckedChange={(v) => { setIsPublic(v); }} />
            </div>
            <div className="flex items-center justify-between py-2">
              <div><p className="font-bold text-sm text-[#2b2620]">Accept notes from anyone</p><p className="text-xs text-[#6b6155]">Off = contacts only</p></div>
              <Switch data-testid="toggle-accept" checked={acceptAnyone} onCheckedChange={setAcceptAnyone} />
            </div>
            {isPublic && (
              <div className="flex items-center justify-between bg-white rounded-xl p-2 mt-2">
                <span className="text-xs font-note text-[#4a4238] truncate">{publicUrl}</span>
                <button data-testid="copy-public" onClick={() => { navigator.clipboard?.writeText(publicUrl); toast.success("Link copied!"); }} className="text-[#6b6155]"><Copy className="w-4 h-4" /></button>
              </div>
            )}
            <p className="text-[11px] text-[#a89a86] mt-2">Remember to hit “Save changes” above to apply privacy settings.</p>
            <div className="flex items-center justify-between py-2 mt-2 border-t border-[#eee3ce]">
              <div><p className="font-bold text-sm text-[#2b2620]">Auto-cleanup</p><p className="text-xs text-[#6b6155]">Automatically crumple notes older than…</p></div>
              <Select value={autoCrumple} onValueChange={setAutoCrumple}>
                <SelectTrigger data-testid="auto-crumple-select" className="w-32 bg-white"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="0" data-testid="auto-crumple-0">Off</SelectItem>
                  <SelectItem value="7" data-testid="auto-crumple-7">7 days</SelectItem>
                  <SelectItem value="14" data-testid="auto-crumple-14">14 days</SelectItem>
                  <SelectItem value="30" data-testid="auto-crumple-30">30 days</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Stats */}
          <ProfileStats stats={stats} />
        </div>
      </div>
      <UpgradeDialog open={upgradeOpen} onClose={() => setUpgradeOpen(false)} />
    </div>
  );
}
